
import React, { useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useToast } from "@/components/ui/use-toast";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

const Pricing = () => {
  const { subscription, isPro, upgradeSubscription, cancelCurrentSubscription } = useSubscription();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const handleUpgrade = async () => {
    try {
      setIsUpgrading(true);
      await upgradeSubscription();
      toast({
        title: "Subscription Upgraded",
        description: "You now have access to all PRO features!",
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsUpgrading(false);
    }
  };

  const handleCancel = async () => {
    try {
      setIsCancelling(true);
      await cancelCurrentSubscription();
      toast({
        title: "Subscription Cancelled",
        description: "Your PRO subscription has been cancelled.",
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleNavigateBack = () => {
    navigate(-1);
  };

  const plans = [
    {
      id: "free",
      name: "Free",
      description: "For individuals and small teams",
      price: 0,
      features: [
        { name: "Create basic events", included: true },
        { name: "Up to 100 registrations per event", included: true },
        { name: "Single ticket type", included: true },
        { name: "Basic analytics", included: true },
        { name: "Email notifications", included: true },
        { name: "Multiple ticket types", included: false },
        { name: "Custom registration fields", included: false },
        { name: "Advanced event analytics", included: false },
        { name: "Priority support", included: false }
      ]
    },
    {
      id: "pro",
      name: "PRO",
      description: "For growing organizations and businesses",
      price: 19,
      popular: true,
      features: [
        { name: "Create unlimited events", included: true },
        { name: "Unlimited registrations", included: true },
        { name: "Multiple ticket types", included: true, highlight: true },
        { name: "Custom registration fields", included: true, highlight: true },
        { name: "Advanced event analytics", included: true },
        { name: "Email notifications", included: true },
        { name: "Custom branding", included: true },
        { name: "Priority support", included: true },
        { name: "API access", included: true }
      ]
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
          <div className="mb-12">
            <Button 
              variant="ghost" 
              onClick={handleNavigateBack} 
              className="mb-6"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            
            <h1 className="text-3xl font-extrabold text-gray-900 sm:text-4xl sm:tracking-tight">
              Simple, transparent pricing
            </h1>
            <p className="mt-4 max-w-2xl text-xl text-gray-500">
              Choose the plan that's right for you
            </p>
          </div>
          
          <div className="grid grid-cols-1 gap-y-12 sm:grid-cols-2 sm:gap-x-6 lg:gap-x-8">
            {plans.map((plan) => (
              <Card 
                key={plan.id} 
                className={`flex flex-col ${plan.popular ? 'border-amber-400 shadow-xl' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-amber-400 text-white px-3 py-1 text-xs font-semibold rounded-bl-md rounded-tr-md">
                    Most Popular
                  </div>
                )}
                
                <CardHeader>
                  <CardTitle>{plan.name}</CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                </CardHeader>
                
                <CardContent className="flex-grow">
                  <div className="mb-6">
                    {plan.price === 0 ? (
                      <p className="text-4xl font-bold">Free</p>
                    ) : (
                      <p className="text-4xl font-bold">
                        ${plan.price}<span className="text-lg font-normal text-gray-500">/month</span>
                      </p>
                    )}
                  </div>
                  
                  <div className="space-y-3">
                    {plan.features.map((feature, index) => (
                      <div key={index} className="flex items-start">
                        {feature.included ? (
                          <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        ) : (
                          <XCircle className="h-5 w-5 text-gray-300 mr-2 flex-shrink-0 mt-0.5" />
                        )}
                        <span className={`${feature.highlight ? 'font-medium' : ''}`}>
                          {feature.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
                
                <CardFooter>
                  {plan.id === "free" ? (
                    <Button 
                      variant="outline" 
                      className="w-full"
                      disabled={!isPro}
                      onClick={handleCancel}
                    >
                      {!isPro ? "Current Plan" : (isCancelling ? "Cancelling..." : "Downgrade to Free")}
                    </Button>
                  ) : (
                    <Button 
                      className={`w-full ${isPro ? 'bg-gray-200 hover:bg-gray-300 text-gray-800' : 'bg-amber-500 hover:bg-amber-600'}`}
                      disabled={isPro || isUpgrading}
                      onClick={handleUpgrade}
                    >
                      {isPro ? "Current Plan" : (isUpgrading ? "Upgrading..." : "Upgrade to PRO")}
                    </Button>
                  )}
                </CardFooter>
              </Card>
            ))}
          </div>
          
          <div className="mt-12 bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-8">
              <h3 className="text-lg font-medium text-gray-900">
                Frequently Asked Questions
              </h3>
              <div className="mt-6 grid gap-6">
                <div>
                  <h4 className="font-medium">Can I cancel my subscription?</h4>
                  <p className="mt-2 text-sm text-gray-500">
                    Yes, you can cancel your subscription at any time. You'll continue to have access to PRO features until the end of your billing period.
                  </p>
                </div>
                <div>
                  <h4 className="font-medium">How do I get a refund?</h4>
                  <p className="mt-2 text-sm text-gray-500">
                    If you're not satisfied with our service, contact our support team within 14 days of purchase for a full refund.
                  </p>
                </div>
                <div>
                  <h4 className="font-medium">What payment methods do you accept?</h4>
                  <p className="mt-2 text-sm text-gray-500">
                    We accept all major credit cards, PayPal, and bank transfers for annual plans.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Pricing;
